import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, message } from 'antd';
import { useAuthStore } from '@stores/auth.store';
import { useCanCheck, useUserMetadata } from '@ability/helpers';
import { PROJECT_CLIENT_VIEW, PROJECT_DETAILS_UPDATE } from '@/ability';
import { useProjectDetail, useProjectDetailsUpdate, useProjectDetailsAttachmentDelete, looksLikeMarkdownDisplay, readFileAsDataUrl, useProjectSummaryAnalytics, useProjectTeamActivity } from '@hooks/projects/detail/useProjectDetail';
import { useProjectContributors } from '@hooks/projects/useProjectContributors';
import { useProjectMilestones } from '@hooks/projects/useProjectMilestones';
import { useProjectSprints } from '@hooks/projects/useProjectSprints';
import { formatDate, linkifyPlainUrlsInHtml, linkifyPlainUrlsForMarkdown, formatProjectType } from '@hooks/projects/projectHelpers';

const MENTION_USERS_LIMIT = 100;

function dataURLtoFile(dataurl, filename) {
    if (!dataurl) return null;
    try {
        var arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    } catch (e) {
        console.error("Failed to convert dataURL to file", e);
        return null;
    }
}

export function useProjectDetailsTabLogic(projectId) {
    const navigate = useNavigate();
    const can = useCanCheck();
    const { role } = useUserMetadata() || {};
    const isClientRole = String(role || '').toLowerCase() === 'client';
    const canEditDetail = can(PROJECT_DETAILS_UPDATE);
    const canViewClientLine = can(PROJECT_CLIENT_VIEW);

    const user = useAuthStore((s) => s.user);
    const currentTenantId =
        user?.tenantId ??
        (Array.isArray(user?.tenants) ? user?.tenants?.[0]?.id ?? null : null);

    const [activeTab, setActiveTab] = useState('info');

    const { data: project, isLoading: projectLoading, isError: projectError } = useProjectDetail(projectId);
    const { data: analytics, isLoading: analyticsLoading } = useProjectSummaryAnalytics(projectId, {
        enabled: activeTab === 'info',
    });
    const { data: teamActivityData } = useProjectTeamActivity(projectId, { page: 1, limit: 5 }, {
        enabled: activeTab === 'info',
    });
    
    const contributors = useProjectContributors(projectId);
    
    const isInvitedCollaborator = useMemo(
        () =>
            !!(
                project?.collaborators?.length &&
                currentTenantId &&
                project.collaborators.some((c) => String(c.invitedTenantId) === String(currentTenantId))
            ),
        [project?.collaborators, currentTenantId],
    );
    const showDetailAndAttachmentActions = canEditDetail && !isInvitedCollaborator;
    const {
        updateProjectDetails: saveDetails,
        updatePending: savePending,
        uploadAttachments,
        uploadAttachmentsPending,
    } = useProjectDetailsUpdate(projectId);
    const deleteAttachmentMutation = useProjectDetailsAttachmentDelete(projectId);
    const [isEditingDetail, setIsEditingDetail] = useState(false);
    const isEditing = isEditingDetail;

    const { data: milestones = [] } = useProjectMilestones(projectId, { enabled: isEditing });
    const { data: projectSprints = [] } = useProjectSprints(projectId, { enabled: isEditing });

    const detailsSubTab = activeTab;
    const setDetailsSubTab = setActiveTab;
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [previewImageUrl, setPreviewImageUrl] = useState('');
    const attachmentInputRef = useRef(null);

    const [editContent, setEditContent] = useState('');
    const [descriptionDraft, setDescriptionDraft] = useState(null);
    const [pendingPostAttachments, setPendingPostAttachments] = useState([]);
    const [pendingLinkAttachments, setPendingLinkAttachments] = useState([]);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkModalForm] = Form.useForm();
    const linkModalCallbackRef = useRef(null);
    const [attachUrlsExpanded, setAttachUrlsExpanded] = useState(false);
    const [attachUrlModalOpen, setAttachUrlModalOpen] = useState(false);
    const [attachUrlForm] = Form.useForm();
    const [membersModalOpen, setMembersModalOpen] = useState(false);
    const [membersSearch, setMembersSearch] = useState('');
    const [isClientChipExpanded, setIsClientChipExpanded] = useState(false);
    const [isDraftInitialized, setIsDraftInitialized] = useState(false);
    const [deletedSavedUrls, setDeletedSavedUrls] = useState(() => new Set());

    const serverContent = project?.projectDetails?.content ?? '';
    const hasServerContent = !!serverContent.trim();

    // Reset initialization state on projectId change
    useEffect(() => {
        setIsDraftInitialized(false);
    }, [projectId]);

    // Initialize/sync descriptionDraft on mount or project load
    useEffect(() => {
        if (projectId) {
            const savedStr = localStorage.getItem(`code360_project_description_draft:${projectId}`);
            if (savedStr !== null) {
                try {
                    const parsed = JSON.parse(savedStr);
                    const serverContentVal = project?.projectDetails?.content ?? '';
                    const contentDiffers = (parsed.content ?? '') !== serverContentVal;
                    const hasLinks = Array.isArray(parsed.links) && parsed.links.length > 0;
                    const hasFiles = Array.isArray(parsed.files) && parsed.files.length > 0;

                    if (contentDiffers || hasLinks || hasFiles) {
                        setDescriptionDraft(parsed);
                    } else {
                        setDescriptionDraft(null);
                    }
                } catch (e) {
                    // Fallback for old plain-text format
                    const serverContentVal = project?.projectDetails?.content ?? '';
                    if (savedStr !== serverContentVal) {
                        setDescriptionDraft({ content: savedStr, links: [], files: [] });
                    } else {
                        setDescriptionDraft(null);
                    }
                }
            } else {
                setDescriptionDraft(null);
            }
        }
    }, [projectId, project?.projectDetails?.content]);

    const projectDetailsContent = project?.projectDetails?.content ?? '';
    const hasContent = !!projectDetailsContent?.trim();
    const projectDetailsContentLinked = useMemo(
        () => linkifyPlainUrlsInHtml(projectDetailsContent),
        [projectDetailsContent],
    );

    const showProjectDetailsAsMarkdown = useMemo(() => {
        const s = String(projectDetailsContent ?? '').trim();
        if (!s) return false;
        if (/<[^>]+>/.test(s)) return false;
        return looksLikeMarkdownDisplay(s);
    }, [projectDetailsContent]);

    const detailsMarkdownAttachmentItems = useMemo(() => {
        const listDetails = project?.projectDetails?.attachments ?? [];
        const listProject = project?.attachments ?? [];
        
        const combined = [];
        const seenUrls = new Set();
        
        // First, add all details attachments to preserve their original indices for markdown parsing
        let idx = 0;
        for (const a of listDetails) {
            const url = a.url ?? a.href ?? a.fileUrl ?? a.path ?? '';
            if (url) {
                seenUrls.add(url);
            }
            combined.push({
                ...a,
                type: String(a?.type || 'file').toLowerCase() === 'link' || url.startsWith('http://') || url.startsWith('https://') ? 'link' : 'file',
                url,
                title: a.title ?? a.name ?? url,
                index: idx,
                isDetailsAttachment: true,
            });
            idx++;
        }
        
        // Then, append project attachments if they are not already in the list
        for (const a of listProject) {
            const url = a.url ?? a.href ?? a.fileUrl ?? a.path ?? '';
            if (!url || seenUrls.has(url)) continue;
            seenUrls.add(url);
            combined.push({
                ...a,
                type: String(a?.type || 'file').toLowerCase() === 'link' || url.startsWith('http://') || url.startsWith('https://') ? 'link' : 'file',
                url,
                title: a.title ?? a.name ?? url,
            });
        }
        
        return combined;
    }, [project?.projectDetails?.attachments, project?.attachments]);

    const handleDeleteSavedAttachment = useCallback((item) => {
        setDeletedSavedUrls((prev) => {
            const next = new Set(prev);
            next.add(item.url);
            return next;
        });

        let newContent = editContent || '';

        // If it is a description (details) attachment, strip by index
        if (item.isDetailsAttachment && item.index !== undefined) {
            const index = item.index;
            
            // Match <a> HTML tags referencing #attachment-index
            const linkRegex = new RegExp(`<a\\s(?=[^>]*href="#attachment-${index}")[^>]*>[\\s\\S]*?</a>`, 'gi');
            newContent = newContent.replace(linkRegex, '');
            
            // Match markdown-style links [title](#attachment-index)
            const mdRegex = new RegExp(`\\[[^\\]]*\\]\\(#attachment-${index}\\)`, 'g');
            newContent = newContent.replace(mdRegex, '');

            // Match HTML image tags
            const imgHtmlRegex = new RegExp(`<img\\s[^>]*src="#attachment-${index}"[^>]*>`, 'gi');
            newContent = newContent.replace(imgHtmlRegex, '');

            // Match markdown-style images ![alt](#attachment-index)
            const mdImgRegex = new RegExp(`!\\[[^\\]]*\\]\\(#attachment-${index}\\)`, 'g');
            newContent = newContent.replace(mdImgRegex, '');

            // Shift indices of subsequent description attachments in editContent
            const detailsCount = (detailsMarkdownAttachmentItems || []).filter(x => x.isDetailsAttachment).length;
            for (let j = index + 1; j < detailsCount; j++) {
                newContent = newContent.replace(new RegExp(`#attachment-${j}`, 'g'), `#attachment-${j - 1}`);
            }
        }

        // Also strip the absolute URL references just in case they are referenced that way
        const escapedUrl = (item.url || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        
        // Match <a> HTML tags referencing this URL
        const urlLinkRegex = new RegExp(`<a\\s(?=[^>]*href="${escapedUrl}")[^>]*>[\\s\\S]*?</a>`, 'gi');
        newContent = newContent.replace(urlLinkRegex, '');
        
        // Match markdown-style links [title](url)
        const escapedTitle = (item.title || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const urlMdRegex = new RegExp(`\\[${escapedTitle}\\]\\(${escapedUrl}\\)`, 'g');
        newContent = newContent.replace(urlMdRegex, '');

        // Match HTML image tags
        const urlImgHtmlRegex = new RegExp(`<img\\s[^>]*src="${escapedUrl}"[^>]*>`, 'gi');
        newContent = newContent.replace(urlImgHtmlRegex, '');

        // Match markdown-style images ![alt](url)
        const urlMdImgRegex = new RegExp(`!\\[[^\\]]*\\]\\(${escapedUrl}\\)`, 'g');
        newContent = newContent.replace(urlMdImgRegex, '');

        setEditContent(newContent);
    }, [editContent, setEditContent, detailsMarkdownAttachmentItems]);

    const mentionUsers = useMemo(() => (project?.assignedUsers ?? []).slice(0, MENTION_USERS_LIMIT), [project?.assignedUsers]);
    const mentionMilestones = useMemo(() => milestones ?? [], [milestones]);
    const mentionSprints = useMemo(() => (Array.isArray(projectSprints) ? projectSprints : []), [projectSprints]);

    // Load draft content, links, and attachments from localStorage upon editing
    useEffect(() => {
        if (isEditing && projectId && !projectLoading && !isDraftInitialized) {
            const savedStr = localStorage.getItem(`code360_project_description_draft:${projectId}`);
            if (savedStr !== null) {
                try {
                    const parsed = JSON.parse(savedStr);
                    setEditContent(parsed.content ?? '');
                    setPendingLinkAttachments(parsed.links ?? []);
                    
                    if (Array.isArray(parsed.files)) {
                        const reconstructed = parsed.files.map((item) => {
                            const file = dataURLtoFile(item.dataUrl, item.name);
                            return {
                                id: item.id,
                                file: file || null,
                                name: item.name,
                                type: item.type,
                                dataUrl: item.dataUrl,
                                preview: (item.type || '').startsWith('image/') ? (item.dataUrl || undefined) : undefined,
                            };
                        });
                        setPendingPostAttachments(reconstructed);
                    } else {
                        setPendingPostAttachments([]);
                    }
                } catch (e) {
                    // Fallback for old plain-text format
                    setEditContent(savedStr);
                    setPendingLinkAttachments([]);
                    setPendingPostAttachments([]);
                }
            } else {
                setEditContent(project?.projectDetails?.content ?? '');
                setPendingPostAttachments([]);
                setPendingLinkAttachments([]);
            }
            setIsDraftInitialized(true);
        }
    }, [isEditing, projectId, projectLoading, project?.projectDetails?.content, isDraftInitialized]);

    // Save draft content, links, and attachments to localStorage when they change
    useEffect(() => {
        if (isEditing && projectId && !projectLoading && isDraftInitialized) {
            const serverContentVal = project?.projectDetails?.content ?? '';
            
            const contentDiffers = (editContent !== undefined && editContent !== serverContentVal);
            const hasLinks = pendingLinkAttachments.length > 0;
            const hasFiles = pendingPostAttachments.length > 0;

            if (contentDiffers || hasLinks || hasFiles) {
                const serializableFiles = pendingPostAttachments.map(p => ({
                    id: p.id,
                    name: p.name,
                    type: p.type,
                    dataUrl: p.dataUrl || '',
                }));

                const draftObj = {
                    content: editContent || '',
                    links: pendingLinkAttachments,
                    files: serializableFiles
                };

                localStorage.setItem(`code360_project_description_draft:${projectId}`, JSON.stringify(draftObj));
                setDescriptionDraft(draftObj);
            } else {
                localStorage.removeItem(`code360_project_description_draft:${projectId}`);
                setDescriptionDraft(null);
            }
        }
    }, [editContent, pendingLinkAttachments, pendingPostAttachments, isEditing, projectId, projectLoading, project?.projectDetails?.content, isDraftInitialized]);

    useEffect(() => {
        if (!isEditing) {
            setPendingPostAttachments([]);
            setPendingLinkAttachments([]);
            setIsDraftInitialized(false);
            setDeletedSavedUrls(new Set());
        }
    }, [isEditing]);

    const assignedUsers = project?.assignedUsers ?? [];
    const visibleAssignedUsers = useMemo(() => {
        const query = membersSearch.trim().toLowerCase();
        if (!query) return assignedUsers;
        return assignedUsers.filter((u) => {
            const name = String(u?.name || '').toLowerCase();
            const email = String(u?.email || '').toLowerCase();
            return name.includes(query) || email.includes(query);
        });
    }, [assignedUsers, membersSearch]);

    const displayAttachments = useMemo(() => {
        const fromDetails = (project?.projectDetails?.attachments ?? []).map((a, i) => ({
            ...a,
            id: a.id ?? `detail-att-${i}`,
            name: a.title ?? a.name,
            url: a.url ?? a.href ?? a.fileUrl ?? a.path,
            _fromDetails: true,
        }));
        const fromProject = (project?.attachments ?? []).map((a) => ({
            ...a,
            url: a.url ?? a.href ?? a.fileUrl ?? a.path,
            _fromProject: true,
        }));
        const pending = pendingAttachments.map((p) => ({
            id: p.id,
            name: p.name,
            url: p.dataUrl,
            type: p.type,
            _pending: true,
        }));
        return [...fromDetails, ...fromProject, ...pending];
    }, [project?.projectDetails?.attachments, project?.attachments, pendingAttachments]);

    const linkAttachments = useMemo(
        () =>
            displayAttachments.filter((a) => {
                const u = a.url ?? a.href ?? a.fileUrl ?? '';
                return typeof u === 'string' && (u.startsWith('http://') || u.startsWith('https://'));
            }),
        [displayAttachments],
    );
    const fileAttachments = useMemo(
        () =>
            displayAttachments.filter((a) => {
                const u = a.url ?? a.href ?? a.fileUrl ?? '';
                return !u || (typeof u === 'string' && !u.startsWith('http://') && !u.startsWith('https://'));
            }),
        [displayAttachments],
    );

    const handleAttachmentAddClick = useCallback(() => {
        attachmentInputRef.current?.click();
    }, []);

    const handleAttachmentFileChange = useCallback(
        (e) => {
            const fileList = e.target?.files;
            if (!fileList?.length) return;
            const files = Array.from(fileList);
            e.target.value = '';
            uploadAttachments(
                { attachmentUrls: [], files },
                {
                    onSuccess: () => message.success('Attachments uploaded'),
                    onError: (err) =>
                        message.error(err?.response?.data?.message || err?.message || 'Failed to upload attachments'),
                },
            );
        },
        [uploadAttachments],
    );

    const handlePostFilesSelected = useCallback(async (files) => {
        if (!files?.length) return;
        const nextPromises = Array.from(files).map(async (file, i) => {
            const isImg = (file.type || '').startsWith('image/');
            let dataUrl = '';
            try {
                dataUrl = await readFileAsDataUrl(file);
            } catch (err) {
                console.error("Failed to read file as data URL", err);
            }
            return {
                id: `detail-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`,
                file,
                name: file.name,
                type: file.type,
                dataUrl: dataUrl || '',
                preview: isImg ? (dataUrl || URL.createObjectURL(file)) : undefined,
            };
        });
        const next = await Promise.all(nextPromises);
        setPendingPostAttachments((prev) => [...prev, ...next]);
    }, []);

    const removePendingPostAttachment = useCallback(
        (id) => {
            const index = pendingPostAttachments.findIndex((p) => p.id === id);
            if (index < 0) {
                setPendingPostAttachments((prev) => prev.filter((p) => p.id !== id));
                return;
            }
            let content = editContent || '';
            const linkRegex = new RegExp(`<a\\s(?=[^>]*href="#attachment-${index}")[^>]*>[\\s\\S]*?</a>`, 'gi');
            content = content.replace(linkRegex, '');
            const total = pendingPostAttachments.length;
            for (let j = total - 1; j > index; j--) {
                content = content.replace(new RegExp(`#attachment-${j}`, 'g'), `#attachment-${j - 1}`);
            }
            setEditContent(content);
            setPendingPostAttachments((prev) => prev.filter((p) => p.id !== id));
        },
        [editContent, pendingPostAttachments]
    );

    const removePendingLinkAttachment = useCallback((id) => {
        const link = pendingLinkAttachments.find((p) => p.id === id);
        if (link) {
            // Remove the corresponding <a> tag from the markdown editor content
            let content = editContent || '';
            // Escape special regex characters in the URL
            const escapedUrl = (link.url || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            // Match <a> tags with this URL
            const linkRegex = new RegExp(`<a\\s(?=[^>]*href="${escapedUrl}")[^>]*>[\\s\\S]*?</a>`, 'gi');
            content = content.replace(linkRegex, '');
            // Also match markdown-style links [title](url)
            const escapedTitle = (link.title || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const mdRegex = new RegExp(`\\[${escapedTitle}\\]\\(${escapedUrl}\\)`, 'g');
            content = content.replace(mdRegex, '');
            setEditContent(content);
        }
        setPendingLinkAttachments((prev) => prev.filter((p) => p.id !== id));
    }, [editContent, pendingLinkAttachments]);

    const handleLinkRequest = useCallback((insertLink) => {
        linkModalCallbackRef.current = insertLink;
        linkModalForm.resetFields();
        setLinkModalOpen(true);
    }, [linkModalForm]);

    const handleAddAttachUrl = useCallback(() => {
        attachUrlForm.resetFields();
        setAttachUrlModalOpen(true);
    }, [attachUrlForm]);

    const handleAttachUrlModalSubmit = useCallback(() => {
        attachUrlForm.validateFields().then((values) => {
            const url = (values.url || '').trim();
            const title = (values.title || '').trim();
            if (!url) {
                message.warning('Enter URL');
                return;
            }
            uploadAttachments(
                { attachmentUrls: [{ type: 'link', url, title: title || url }], files: [] },
                {
                    onSuccess: () => {
                        message.success('Link added');
                        setAttachUrlModalOpen(false);
                        attachUrlForm.resetFields();
                    },
                    onError: (err) =>
                        message.error(err?.response?.data?.message || err?.message || 'Failed to add link'),
                },
            );
        });
    }, [attachUrlForm, uploadAttachments]);

    const handleLinkModalSubmit = useCallback(() => {
        linkModalForm.validateFields().then((values) => {
            const url = (values.url || '').trim();
            const title = (values.title || '').trim();
            if (!url) {
                message.warning('Enter URL');
                return;
            }
            const insertLink = linkModalCallbackRef.current;
            if (insertLink) insertLink({ url, title: title || url });
            setPendingLinkAttachments((prev) => [...prev, { id: `link-${Date.now()}`, url, title: title || url }]);
            setLinkModalOpen(false);
            linkModalCallbackRef.current = null;
        });
    }, [linkModalForm]);

    const handleSaveDetail = useCallback(async () => {
        try {
            // Get original saved attachments from description and filter out deleted ones
            const originalAttachments = project?.projectDetails?.attachments ?? [];
            const remainingSaved = originalAttachments
                .filter((a) => {
                    const url = a.url ?? a.href ?? a.fileUrl ?? a.path;
                    return !deletedSavedUrls.has(url);
                })
                .map((a) => {
                    const url = a.url ?? a.href ?? a.fileUrl ?? a.path;
                    const isLink = String(a.type || '').toLowerCase() === 'link' || url.startsWith('http://') || url.startsWith('https://');
                    return {
                        type: isLink ? 'link' : 'file',
                        url,
                        title: a.title ?? a.name ?? url,
                    };
                });

            // Map new pending link attachments
            const mappedNewLinks = pendingLinkAttachments.map(({ url, title }) => ({
                type: 'link',
                url,
                title: title || url,
            }));

            // Combine both sets
            const attachments = [...remainingSaved, ...mappedNewLinks];
            const files = pendingPostAttachments.map((p) => p.file).filter(Boolean);

            await saveDetails({
                content: editContent || '',
                attachments,
                files,
            });
            message.success('Details saved');
            setIsEditingDetail(false);
            localStorage.removeItem(`code360_project_description_draft:${projectId}`);
            setDescriptionDraft(null);
        } catch (err) {
            message.error(err?.response?.data?.message || err?.message || 'Failed to save');
        }
    }, [saveDetails, editContent, pendingLinkAttachments, pendingPostAttachments, projectId, project?.projectDetails?.attachments, deletedSavedUrls]);

    const handleCancelEdit = useCallback(() => {
        setIsEditingDetail(false);
        setEditContent(project?.projectDetails?.content ?? '');
        setPendingPostAttachments([]);
        setPendingLinkAttachments([]);
    }, [project?.projectDetails?.content]);

    const handleResetDraft = useCallback(() => {
        localStorage.removeItem(`code360_project_description_draft:${projectId}`);
        setDescriptionDraft(null);
        setEditContent(project?.projectDetails?.content ?? '');
        setPendingPostAttachments([]);
        setPendingLinkAttachments([]);
        message.success('Description draft reset to original');
    }, [projectId, project?.projectDetails?.content]);

    const progressPercent = project?.progress ?? 0;
    const createdAtStr = formatDate(project?.createdAt ?? project?.created_at);
    const datesDisplay = createdAtStr || '—';
    const clientUser = project?.client || {};
    const statusLower = String(project?.status || '').toLowerCase();
    const showStatusClock = /pending|invited|awarded|open|todo/.test(statusLower);

    const formattedProjectType = useMemo(() => {
        return formatProjectType(project?.projectType);
    }, [project?.projectType]);

    return {
        navigate,
        projectLoading,
        projectError,
        project,
        isClientRole,
        canViewClientLine,
        isInvitedCollaborator,
        showDetailAndAttachmentActions,
        savePending,
        uploadAttachmentsPending,
        activeTab,
        setActiveTab,
        previewImageUrl,
        setPreviewImageUrl,
        attachmentInputRef,
        isEditingDetail,
        setIsEditingDetail,
        editContent,
        setEditContent,
        pendingPostAttachments,
        pendingLinkAttachments,
        removePendingLinkAttachment,
        linkModalOpen,
        setLinkModalOpen,
        linkModalForm,
        attachUrlsExpanded,
        setAttachUrlsExpanded,
        attachUrlModalOpen,
        setAttachUrlModalOpen,
        attachUrlForm,
        membersModalOpen,
        setMembersModalOpen,
        membersSearch,
        setMembersSearch,
        isClientChipExpanded,
        setIsClientChipExpanded,
        hasContent,
        projectDetailsContentLinked,
        showProjectDetailsAsMarkdown,
        detailsMarkdownAttachmentItems,
        mentionUsers,
        mentionMilestones,
        mentionSprints,
        assignedUsers,
        visibleAssignedUsers,
        linkAttachments,
        fileAttachments,
        handleAttachmentAddClick,
        handleAttachmentFileChange,
        handlePostFilesSelected,
        removePendingPostAttachment,
        handleLinkRequest,
        handleAddAttachUrl,
        handleAttachUrlModalSubmit,
        handleLinkModalSubmit,
        handleSaveDetail,
        handleCancelEdit,
        progressPercent,
        datesDisplay,
        clientUser,
        showStatusClock,
        projectDetailsContent,
        contributors,
        descriptionDraft,
        handleResetDraft,
        hasServerContent,
        isEditing,
        deletedSavedUrls,
        handleDeleteSavedAttachment,
        deleteAttachmentMutation,
        formattedProjectType,
        analytics,
        analyticsLoading,
        teamActivityData,
    };
}
